import { useEffect, useState } from 'react';
import { bookings } from '@repletoryreach/sdk-api';
import { Button, Divider, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { Checkbox, NumberInput, SegmentedControl, Select, Switch, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';
import { ColourPicker } from '../components/ColourPicker';
import { bookingErrorText, SWATCHES } from '../lib/data';
import { parseHhmm, questionKey } from '../lib/time';

const MUTED = 'var(--rr-color-text-muted)';
const MAX_QUESTIONS = 20;
const MAX_WINDOWS = 8;

interface QuestionDraft extends bookings.Question {
  /** Saved questions keep their key: answers already given are stored under it. */
  saved: boolean;
  optionsText: string;
}

interface Draft {
  name: string;
  description: string;
  kind: bookings.ServiceKind;
  duration_minutes: number | '';
  buffer_before_minutes: number | '';
  buffer_after_minutes: number | '';
  colour: string | null;
  price_label: string;
  approval: '' | 'always' | 'never';
  staff_mode: bookings.StaffMode;
  location_kind: bookings.LocationKind;
  online_link: string;
  capacity: number | '';
  callout_windows: bookings.CalloutWindow[];
  questions: QuestionDraft[];
  staff_ids: string[];
  sort_order: number | '';
  active: boolean;
}

function toDraft(s: bookings.Service | null): Draft {
  return {
    name: s?.name ?? '',
    description: s?.description ?? '',
    kind: s?.kind ?? 'appointment',
    duration_minutes: s?.duration_minutes ?? 30,
    buffer_before_minutes: s?.buffer_before_minutes ?? 0,
    buffer_after_minutes: s?.buffer_after_minutes ?? 0,
    colour: s?.colour ?? SWATCHES[0],
    price_label: s?.price_label ?? '',
    approval: s?.requires_approval === true ? 'always' : s?.requires_approval === false ? 'never' : '',
    staff_mode: s?.staff_mode ?? 'any',
    location_kind: s?.location_kind ?? 'business',
    online_link: s?.online_link ?? '',
    capacity: s?.capacity ?? (s?.kind === 'appointment' ? 1 : 10),
    callout_windows: s?.callout_windows?.length ? s.callout_windows : [{ from: '08:00', to: '12:00', capacity: 3 }, { from: '12:00', to: '17:00', capacity: 3 }],
    questions: (s?.questions ?? []).map((q) => ({ ...q, saved: true, optionsText: (q.options ?? []).join(', ') })),
    staff_ids: s?.staff_ids ?? [],
    sort_order: s?.sort_order ?? 0,
    active: s?.active ?? true
  };
}

const num = (v: number | '', fallback = 0) => (v === '' ? fallback : v);

/** The first problem with the draft, as a catalogue key, or null. */
export function serviceProblem(d: Draft): string | null {
  if (!d.name.trim()) return 'bookings.services.error.name';
  if (d.kind !== 'callout' && num(d.duration_minutes) < 5) return 'bookings.services.error.duration';
  if (d.kind === 'class' && num(d.capacity) < 1) return 'bookings.services.error.capacity';
  if (d.kind === 'appointment' && d.staff_mode === 'none' && num(d.capacity) < 1) return 'bookings.services.error.atOnce';
  if (d.location_kind === 'online' && d.online_link.trim() && !/^https?:\/\//i.test(d.online_link.trim())) return 'bookings.services.error.link';
  if (d.kind === 'callout') {
    if (d.callout_windows.length === 0) return 'bookings.services.error.noWindows';
    for (const w of d.callout_windows) {
      const a = parseHhmm(w.from);
      const b = parseHhmm(w.to);
      if (a === null || b === null || a >= b) return 'bookings.services.error.windowTimes';
      if (w.capacity < 1) return 'bookings.services.error.windowCapacity';
    }
  }
  for (const q of d.questions) {
    if (!q.label.trim()) return 'bookings.services.error.questionLabel';
    if (q.type === 'select' && q.optionsText.split(',').map((o) => o.trim()).filter(Boolean).length < 2) return 'bookings.services.error.options';
  }
  return null;
}

function toPayload(d: Draft): Partial<bookings.ServiceInput> & { name: string; kind: bookings.ServiceKind } {
  return {
    name: d.name.trim(),
    description: d.description.trim() || null,
    kind: d.kind,
    duration_minutes: num(d.duration_minutes, 30),
    buffer_before_minutes: num(d.buffer_before_minutes),
    buffer_after_minutes: num(d.buffer_after_minutes),
    colour: d.colour,
    price_label: d.price_label.trim() || null,
    requires_approval: d.approval === 'always' ? true : d.approval === 'never' ? false : null,
    staff_mode: d.kind === 'appointment' ? d.staff_mode : 'any',
    location_kind: d.kind === 'callout' ? 'client' : d.location_kind,
    online_link: d.location_kind === 'online' ? d.online_link.trim() || null : null,
    capacity: d.kind === 'class' || (d.kind === 'appointment' && d.staff_mode === 'none') ? num(d.capacity, 1) : null,
    callout_windows: d.kind === 'callout' ? d.callout_windows : [],
    questions: d.questions.map((q) => ({
      key: q.key,
      label: q.label.trim(),
      type: q.type,
      required: q.required,
      ...(q.type === 'select' ? { options: q.optionsText.split(',').map((o) => o.trim()).filter(Boolean) } : {})
    })),
    sort_order: num(d.sort_order),
    active: d.active
  };
}

export function ServiceDrawer({ target, staff, canEdit, onClose, onSaved }: {
  target: bookings.Service | 'new' | null;
  staff: bookings.Staff[];
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const existing = target && target !== 'new' ? target : null;
  const open = target !== null;
  const [d, setD] = useState<Draft>(() => toDraft(null));
  const [initial, setInitial] = useState('');
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const draft = toDraft(existing);
    setD(draft);
    setInitial(JSON.stringify(draft));
    setTried(false);
    setError(null);
  }, [open, existing?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const dirty = open && JSON.stringify(d) !== initial;
  const problem = serviceProblem(d);

  function requestClose() {
    if (!dirty || !canEdit) { onClose(); return; }
    openConfirmModal({
      title: t('bookings.discard.title'),
      children: <Text role="bodySmall">{t('bookings.discard.body')}</Text>,
      labels: { confirm: t('action.discard'), cancel: t('action.keep') },
      confirmProps: { color: 'red' },
      onConfirm: onClose
    });
  }

  async function save() {
    setTried(true);
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      const payload = toPayload(d);
      const saved = existing
        ? (await bookings.updateService(existing.id, payload)).data
        : (await bookings.createService(payload)).data;
      const before = [...(existing?.staff_ids ?? [])].sort().join(',');
      if ([...d.staff_ids].sort().join(',') !== before) await bookings.setServiceStaff(saved.id, d.staff_ids);
      notifySuccess(t(existing ? 'bookings.services.toast.saved' : 'bookings.services.toast.created', { name: saved.name }), t('bookings.services.title'));
      onSaved();
      onClose();
    } catch (e) {
      setError(bookingErrorText(t, e));
    } finally {
      setBusy(false);
    }
  }

  function setQuestion(i: number, patch: Partial<QuestionDraft>) {
    setD((x) => {
      const questions = x.questions.map((q, j) => {
        if (j !== i) return q;
        const next = { ...q, ...patch };
        if (patch.label !== undefined && !q.saved) {
          next.key = questionKey(patch.label, x.questions.filter((_, k) => k !== i).map((o) => o.key));
        }
        return next;
      });
      return { ...x, questions };
    });
  }

  function addQuestion() {
    setD((x) => ({
      ...x,
      questions: [...x.questions, { key: questionKey('', x.questions.map((q) => q.key)), label: '', type: 'text', required: false, saved: false, optionsText: '' }]
    }));
  }

  const setWindow = (i: number, patch: Partial<bookings.CalloutWindow>) =>
    set({ callout_windows: d.callout_windows.map((w, j) => (j === i ? { ...w, ...patch } : w)) });

  const ro = !canEdit;
  const activeStaff = staff.filter((p) => p.active || d.staff_ids.includes(p.id));

  return (
    <Drawer opened={open} onClose={requestClose} title={existing ? existing.name : t('bookings.services.new')} size="lg">
      <form onSubmit={(e) => { e.preventDefault(); if (canEdit) void save(); }}>
        <Stack gap="md" data-testid="service-editor">
          {ro && <Alert color="info">{t('bookings.readOnly')}</Alert>}

          <div data-guide="service-name">
            <TextInput label={t('common.name')} value={d.name} onChange={(e) => set({ name: e.currentTarget.value })} required maxLength={120} disabled={ro}
              error={tried && problem === 'bookings.services.error.name' ? t(problem) : undefined} />
          </div>
          <Textarea label={t('common.description')} value={d.description} onChange={(e) => set({ description: e.currentTarget.value })} minRows={2} maxLength={1000} disabled={ro}
            description={t('bookings.services.descriptionHint')} />

          <Stack gap="xs" data-guide="service-kind">
            <Text role="inputLabel">{t('bookings.services.kind')}</Text>
            <SegmentedControl
              value={d.kind}
              onChange={(v) => set({ kind: v as bookings.ServiceKind })}
              disabled={ro || !!existing}
              data={[
                { value: 'appointment', label: t('bookings.kind.appointment') },
                { value: 'class', label: t('bookings.kind.class') },
                { value: 'callout', label: t('bookings.kind.callout') }
              ]}
            />
            <Text role="caption" c={MUTED}>{t(`bookings.services.kindHint.${d.kind}`)}{existing ? ` ${t('bookings.services.kindLocked')}` : ''}</Text>
          </Stack>

          {d.kind !== 'callout' && (
            <Group gap="sm" grow align="flex-start" wrap="wrap" data-guide="service-length">
              <NumberInput label={t('bookings.services.duration')} value={d.duration_minutes} onChange={(v) => set({ duration_minutes: v })} min={5} max={1440} step={5} disabled={ro}
                description={t('bookings.services.minutesHint')}
                error={tried && problem === 'bookings.services.error.duration' ? t(problem) : undefined} />
              <NumberInput label={t('bookings.services.bufferBefore')} value={d.buffer_before_minutes} onChange={(v) => set({ buffer_before_minutes: v })} min={0} max={240} step={5} disabled={ro}
                description={t('bookings.services.bufferHint')} />
              <NumberInput label={t('bookings.services.bufferAfter')} value={d.buffer_after_minutes} onChange={(v) => set({ buffer_after_minutes: v })} min={0} max={240} step={5} disabled={ro} />
            </Group>
          )}

          {d.kind === 'class' && (
            <NumberInput label={t('bookings.services.capacity')} value={d.capacity} onChange={(v) => set({ capacity: v })} min={1} max={10000} disabled={ro}
              description={t('bookings.services.capacityHint')}
              error={tried && problem === 'bookings.services.error.capacity' ? t(problem) : undefined} />
          )}

          {d.kind === 'callout' && (
            <Stack gap="xs" data-testid="callout-windows">
              <Text role="inputLabel">{t('bookings.services.windows')}</Text>
              {d.callout_windows.map((w, i) => (
                <Group key={i} gap="xs" align="flex-end" wrap="wrap">
                  <TextInput type="time" label={t('bookings.services.windowFrom')} value={w.from} onChange={(e) => setWindow(i, { from: e.currentTarget.value })} disabled={ro} />
                  <TextInput type="time" label={t('bookings.services.windowTo')} value={w.to} onChange={(e) => setWindow(i, { to: e.currentTarget.value })} disabled={ro} />
                  <NumberInput label={t('bookings.services.windowCapacity')} value={w.capacity} onChange={(v) => setWindow(i, { capacity: v === '' ? 0 : v })} min={1} max={100} disabled={ro} />
                  {canEdit && d.callout_windows.length > 1 && (
                    <Button size="xs" variant="subtle" color="red" onClick={() => set({ callout_windows: d.callout_windows.filter((_, j) => j !== i) })}
                      aria-label={t('bookings.services.removeWindow', { n: i + 1 })}>
                      {t('action.remove')}
                    </Button>
                  )}
                </Group>
              ))}
              <Text role="caption" c={MUTED}>{t('bookings.services.windowsHint')}</Text>
              {tried && (problem === 'bookings.services.error.windowTimes' || problem === 'bookings.services.error.windowCapacity' || problem === 'bookings.services.error.noWindows') && (
                <Text role="caption" c="var(--rr-color-error)">{t(problem)}</Text>
              )}
              {canEdit && d.callout_windows.length < MAX_WINDOWS && (
                <div>
                  <Button size="xs" variant="subtle" onClick={() => set({ callout_windows: [...d.callout_windows, { from: '13:00', to: '17:00', capacity: 3 }] })}>
                    {t('bookings.services.addWindow')}
                  </Button>
                </div>
              )}
            </Stack>
          )}

          {d.kind !== 'callout' && (
            <Stack gap="xs" data-guide="service-where">
              <Select
                label={t('bookings.services.location')}
                value={d.location_kind}
                onChange={(e) => set({ location_kind: e.currentTarget.value as bookings.LocationKind })}
                disabled={ro}
                data={[
                  { value: 'business', label: t('bookings.location.business') },
                  { value: 'client', label: t('bookings.location.client') },
                  { value: 'online', label: t('bookings.location.online') }
                ]}
                description={t('bookings.services.locationHint')}
              />
              {d.location_kind === 'online' && (
                <TextInput type="url" label={t('bookings.services.onlineLink')} value={d.online_link} onChange={(e) => set({ online_link: e.currentTarget.value })} disabled={ro}
                  description={t('bookings.services.onlineLinkHint')}
                  error={tried && problem === 'bookings.services.error.link' ? t(problem) : undefined} />
              )}
            </Stack>
          )}

          <TextInput label={t('bookings.services.price')} value={d.price_label} onChange={(e) => set({ price_label: e.currentTarget.value })} maxLength={60} disabled={ro}
            description={t('bookings.services.priceHint')} />

          <ColourPicker label={t('bookings.services.colour')} value={d.colour} onChange={(c) => set({ colour: c })} disabled={ro} />

          <div data-guide="service-approval">
          <Select
            label={t('bookings.services.approval')}
            value={d.approval}
            onChange={(e) => set({ approval: e.currentTarget.value as Draft['approval'] })}
            disabled={ro}
            data={[
              { value: '', label: t('bookings.services.approval.default') },
              { value: 'always', label: t('bookings.services.approval.always') },
              { value: 'never', label: t('bookings.services.approval.never') }
            ]}
            description={t('bookings.services.approvalHint')}
          />
          </div>

          <Divider />

          {d.kind === 'appointment' && (
            <div data-guide="service-who">
            <Select
              label={t('bookings.services.staffMode')}
              value={d.staff_mode}
              onChange={(e) => {
                const mode = e.currentTarget.value as bookings.StaffMode;
                // A service switched to nobody in particular starts at one at a time, not a class's ten places.
                set(mode === 'none' && !(target && target !== 'new' && target.capacity) ? { staff_mode: mode, capacity: 1 } : { staff_mode: mode });
              }}
              disabled={ro}
              data={[
                { value: 'any', label: t('bookings.services.staffMode.any') },
                { value: 'choose', label: t('bookings.services.staffMode.choose') },
                { value: 'none', label: t('bookings.services.staffMode.none') }
              ]}
              description={d.staff_mode === 'none' ? t('bookings.services.staffModeNoneHint') : t('bookings.services.staffModeHint')}
              data-testid="staff-mode"
            />
            </div>
          )}

          {d.kind === 'appointment' && d.staff_mode === 'none' ? (
            <div data-guide="service-at-once">
            <NumberInput label={t('bookings.services.atOnce')} value={d.capacity} onChange={(v) => set({ capacity: v })} min={1} max={10000} disabled={ro}
              description={t('bookings.services.atOnceHint')}
              error={tried && problem === 'bookings.services.error.atOnce' ? t(problem) : undefined}
              data-testid="at-once" />
            </div>
          ) : (
            <Stack gap="xs" data-guide="service-people">
              <Text role="inputLabel">{t('bookings.services.people')}</Text>
              {activeStaff.length === 0 ? (
                <Text role="bodySmall" c={MUTED}>{t('bookings.services.noPeople')}</Text>
              ) : activeStaff.map((p) => (
                <Checkbox
                  key={p.id}
                  label={p.name}
                  checked={d.staff_ids.includes(p.id)}
                  disabled={ro}
                  onChange={(on) => set({ staff_ids: on ? [...d.staff_ids, p.id] : d.staff_ids.filter((x) => x !== p.id) })}
                />
              ))}
              <Text role="caption" c={MUTED}>{t('bookings.services.peopleHint')}</Text>
            </Stack>
          )}

          <Divider />

          <Stack gap="sm" data-testid="questions-editor" data-guide="service-questions">
            <Stack gap={2}>
              <Text role="inputLabel">{t('bookings.services.questions')}</Text>
              <Text role="caption" c={MUTED}>{t('bookings.services.questionsHint')}</Text>
            </Stack>
            {d.questions.map((q, i) => (
              <Stack key={i} gap="xs" className="rr-bookings__row">
                <Group gap="xs" align="flex-end" wrap="wrap">
                  <TextInput label={t('bookings.services.questionLabel')} value={q.label} onChange={(e) => setQuestion(i, { label: e.currentTarget.value })} maxLength={120} disabled={ro}
                    style={{ flex: '1 1 200px' }} />
                  <Select
                    label={t('common.type')}
                    value={q.type}
                    onChange={(e) => setQuestion(i, { type: e.currentTarget.value as bookings.QuestionType })}
                    disabled={ro}
                    data={bookings.QUESTION_TYPES.map((ty) => ({ value: ty, label: t(`bookings.questionType.${ty}`) }))}
                  />
                </Group>
                {q.type === 'select' && (
                  <TextInput label={t('bookings.services.options')} value={q.optionsText} onChange={(e) => setQuestion(i, { optionsText: e.currentTarget.value })} disabled={ro}
                    description={t('bookings.services.optionsHint')} />
                )}
                <Group gap="sm" justify="space-between" wrap="wrap">
                  <Checkbox label={t('common.required')} checked={q.required} onChange={(on) => setQuestion(i, { required: on })} disabled={ro} />
                  <Group gap="xs">
                    <Text role="caption" c={MUTED}>{t('bookings.services.questionKey', { key: q.key })}</Text>
                    {canEdit && (
                      <Button size="xs" variant="subtle" color="red" onClick={() => set({ questions: d.questions.filter((_, j) => j !== i) })}
                        aria-label={t('bookings.services.removeQuestion', { label: q.label || q.key })}>
                        {t('action.remove')}
                      </Button>
                    )}
                  </Group>
                </Group>
              </Stack>
            ))}
            {tried && (problem === 'bookings.services.error.questionLabel' || problem === 'bookings.services.error.options') && (
              <Text role="caption" c="var(--rr-color-error)">{t(problem)}</Text>
            )}
            {canEdit && d.questions.length < MAX_QUESTIONS && (
              <div><Button size="xs" variant="subtle" onClick={addQuestion}>{t('bookings.services.addQuestion')}</Button></div>
            )}
          </Stack>

          <Divider />

          <Group gap="sm" grow align="flex-start" wrap="wrap">
            <NumberInput label={t('bookings.services.sortOrder')} value={d.sort_order} onChange={(v) => set({ sort_order: v })} min={0} max={1000} disabled={ro}
              description={t('bookings.services.sortOrderHint')} />
            <Switch label={t('bookings.services.active')} description={t('bookings.services.activeHint')} checked={d.active} onChange={(on) => set({ active: on })} disabled={ro} />
          </Group>

          {error && <Alert color="error" role="alert">{error}</Alert>}

          <Group gap="sm" justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }} data-guide="service-save">
            <Button variant="default" onClick={requestClose}>{canEdit ? t('action.cancel') : t('action.close')}</Button>
            {canEdit && <Button type="submit" loading={busy}>{existing ? t('action.saveChanges') : t('bookings.services.create')}</Button>}
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
